import { useEffect, useState } from "react";
import { Button, Modal, Nav } from "react-bootstrap";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";

import { api_without_cancellation } from "../../api/api";
import { createInsurance, editInsurance } from "../../Redux/insurance/insuranceSlice";
import { formatDateForModalFields, getCaseId, getClientId } from "../../Utils/helper";
import InsuranceCompanyFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceCompanyFields";
import InsuranceAdjusterFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceAdjusterFields";
import InsuranceSupervisorFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceSupervisorFields";
import InsuranceLienAdjusterFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceLienAdjusterFields";
import InsuranceLienInformationFields from "../DefendantPage/DefandantMain/modals/fields/insurance/InsuranceLienInformationFields";
import InsurancePartyFieldset from "../DefendantPage/DefandantMain/modals/fields/insurance/components/InsurancePartyFieldSet";

import "./AddInsurancePopup.css";

const STEPS = [
  { id: "parties", label: "Parties" },
  { id: "type", label: "Insurance Type" },
  { id: "company", label: "Company" },
  { id: "adjuster", label: "Adjuster" },
  { id: "claim", label: "Claim" },
  { id: "lien", label: "Lien" },
];

const PARTY_GROUPS = [
  { type: "Client", label: "Clients", icon: "ic-client" },
  { type: "Defendant", label: "Defendants", icon: "ic-defendants" },
  { type: "OtherParty", label: "Other Parties", icon: "ic-parties" },
];

const partyName = (party) => {
  const last = party.party_last_name || party.witness_last_name || party.last_name || "";
  const first = party.party_first_name || party.witness_first_name || party.first_name || "";
  return [last, first].filter(Boolean).join(", ") || party.entity_name || "Unnamed party";
};

const partyButtonName = (party) => {
  const first = party.party_first_name || party.witness_first_name || party.first_name || "";
  const last = party.party_last_name || party.witness_last_name || party.last_name || "";
  return [first, last].filter(Boolean).join(" ") || party.entity_name || "Unnamed party";
};

const withPartyType = (name, type) => (type ? `${name} - ${type}` : name);

const normalizePartyType = (type) => {
  const key = String(type || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  return {
    client: "Client",
    clients: "Client",
    defendant: "Defendant",
    defendants: "Defendant",
    witness: "Witness",
    witnesses: "Witness",
    otherparty: "OtherParty",
    otherparties: "OtherParty",
    party: "OtherParty",
  }[key];
};

const partyId = (value) => (value && typeof value === "object" ? (value.id ?? value.pk) : value);

const getPartyIdentity = (insurance) => {
  if (!insurance) return null;
  const type =
    normalizePartyType(insurance.entity_type) ||
    (insurance.defendant_id || insurance.defendant ? "Defendant" : null) ||
    (insurance.party_id || insurance.party || insurance.other_party ? "OtherParty" : null) ||
    (insurance.witness_id || insurance.witness ? "Witness" : null);
  if (!type) return null;
  const related = {
    Client: [insurance.for_client, insurance.client, insurance.client_id],
    Defendant: [insurance.defendant_id, insurance.defendant],
    Witness: [insurance.witness_id, insurance.witness],
    OtherParty: [insurance.party_id, insurance.party, insurance.other_party],
  }[type];
  const ids = [insurance.entity_obj, insurance.entity_obj_id, insurance.entity_id, ...related]
    .map(partyId)
    .filter((id) => id !== null && id !== undefined && id !== "");
  return { type, ids };
};

const normalizedName = (name) =>
  String(name || "")
    .toLocaleLowerCase()
    .replace(/[\s,.-]/g, "");

const findPartyForInsurance = (
  availableParties,
  insurance,
  initialParty,
  partyScope,
  preferInitialParty
) => {
  const contextParty = initialParty?.id
    ? availableParties.find(
        (party) =>
          party.type === normalizePartyType(initialParty.type) &&
          String(party.id) === String(initialParty.id)
      )
    : null;
  if (preferInitialParty && contextParty) return contextParty;

  const identity = getPartyIdentity(insurance);
  if (identity) {
    const sameType = availableParties.filter((party) => party.type === identity.type);
    const byId = sameType.find((party) =>
      identity.ids.some((id) => String(id) === String(party.id))
    );
    if (byId) return byId;

    const entity = insurance?.entity_obj;
    const entityName =
      entity && typeof entity === "object"
        ? entity.entity_name || partyButtonName(entity)
        : insurance?.entity_name;
    if (entityName && entityName !== "Unnamed party") {
      const matchingNames = sameType.filter(
        (party) => normalizedName(party.matchName) === normalizedName(entityName)
      );
      if (matchingNames.length === 1) return matchingNames[0];
    }
  }

  if (contextParty) return contextParty;
  if (partyScope === "client" && availableParties.length === 1) return availableParties[0];
  return null;
};

const getParties = (data) => [
  ...(data?.clients || []).map((party) => ({
    id: party.id,
    type: "Client",
    name: partyButtonName(party),
    matchName: partyButtonName(party),
  })),
  ...(data?.defendants || []).map((party) => ({
    id: party.id,
    type: "Defendant",
    name: withPartyType(party.entity_name || partyButtonName(party), party.defendantType?.name),
    matchName: party.entity_name || partyButtonName(party),
  })),
  ...(data?.other_parties || []).map((party) => ({
    id: party.id,
    type: "OtherParty",
    name: withPartyType(party.entity_name || partyButtonName(party), party.party_type_ID?.name),
    matchName: party.entity_name || partyButtonName(party),
  })),
];

const CLAIM_FIELDS = [
  { name: "policy_number", label: "Policy Number" },
  { name: "claim_number", label: "Claim Number" },
  { name: "liability_limit", label: "Liability Limit", type: "number" },
  { name: "liability_limit_all", label: "Liability Limit All", type: "number" },
  { name: "confirmed_date", label: "Coverage Confirmed", type: "date" },
];

const getInsuranceTypeId = (insurance) => {
  const value =
    insurance?.insurance_type?.id ?? insurance?.insurance_type_id ?? insurance?.insurance_type;
  return Number(value) > 0 ? Number(value) : null;
};

const contactValues = (prefix, contact = {}, directoryId = null) => ({
  [`${prefix}_dir_id`]: directoryId,
  [`${prefix}_name`]: contact?.name || "",
  [`${prefix}_first_name`]: contact?.first_name || "",
  [`${prefix}_last_name`]: contact?.last_name || "",
  [`${prefix}_address1`]: contact?.address1 || "",
  [`${prefix}_address2`]: contact?.address2 || "",
  [`${prefix}_city`]: contact?.city || "",
  [`${prefix}_state`]: contact?.state || "",
  [`${prefix}_zip`]: contact?.zip || "",
  [`${prefix}_phone`]: contact?.phone_number || "",
  [`${prefix}_extension`]: contact?.phone_ext || "",
  [`${prefix}_fax`]: contact?.fax || "",
  [`${prefix}_email`]: contact?.email || "",
  [`${prefix}_website`]: contact?.website || "",
});

const getFormValues = (insurance) => ({
  insurance: {
    1: insurance?.id
      ? {
          ...contactValues(
            "company",
            insurance.company_contact,
            insurance.company_contact?.company?.company_directory
          ),
          ...contactValues(
            "adjuster",
            insurance.adjuster,
            insurance.adjuster_ID?.adjuster_directory
          ),
          ...contactValues(
            "supervisor_adjuster",
            insurance.supervisor,
            insurance.supervisor_ID?.adjuster_directory
          ),
          ...contactValues(
            "lien_adjuster",
            insurance.lien_adjuster?.contact,
            insurance.lien_adjuster?.adjuster_directory?.id
          ),
          ...contactValues(
            "lien_holder",
            insurance.lein_company?.contact,
            insurance.lein_company?.company_directory
          ),
          lien_adjuster_id: insurance.lien_adjuster?.adjuster?.id,
          lien_holder_id: insurance.lein_company?.id,
          insurance_type_id: getInsuranceTypeId(insurance),
          policy_number: insurance.policy_number || "",
          claim_number: insurance.claim_number || "",
          liability_limit: insurance.liabilityLimit || "",
          liability_limit_all: insurance.liabilityLimitAll || "",
          confirmed_date: insurance.Dateconfirmedactive
            ? formatDateForModalFields(insurance.Dateconfirmedactive)
            : "",
          ERISAYN: Boolean(insurance.ERISAYN),
          total_paid: insurance.insurancelien_insurance?.totalpaid || "",
          original_lien: insurance.insurancelien_insurance?.liens || "",
          final_lien: insurance.insurancelien_insurance?.lienfinal || "",
        }
      : { insurance_type_id: null },
  },
});

const getSelectedParty = (insurance, initialParty, preferInitialParty = false) => {
  if (preferInitialParty && initialParty?.id) return initialParty;
  const identity = getPartyIdentity(insurance);
  return identity?.ids.length
    ? {
        id: identity.ids[0],
        type: identity.type,
        name: partyName(insurance.entity_obj || {}),
      }
    : initialParty;
};

const getInitialTab = (tab) => {
  if (tab === "entity") return "parties";
  if (tab === "insurance") return "claim";
  if (tab === "supervisor") return "adjuster";
  if (["lien_adjuster", "lien_holder"].includes(tab)) return "lien";
  return STEPS.some((step) => step.id === tab) ? tab : "parties";
};

const ClaimFields = () => {
  const { register } = useFormContext();
  return (
    <div
      className="add-insurance-claim-fields"
      style={{
        padding: "5px 5px 0px 5px",
      }}
    >
      {CLAIM_FIELDS.map((field) => (
        <label className="add-insurance-claim-field mb-0" key={field.name}>
          <span
            className="text-primary-25"
            style={{
              fontWeight: "600",
            }}
          >
            {field.label}:
          </span>
          <input
            type={field.type || "text"}
            step={field.type === "number" ? "0.01" : undefined}
            className="form-control height-25 rounded-0"
            {...register(`insurance.1.${field.name}`)}
          />
        </label>
      ))}
    </div>
  );
};

const LienHolderFields = ({ states }) => {
  const { register, setValue, control } = useFormContext();
  const [name, firstName, lastName] = useWatch({
    control,
    name: [
      "insurance.1.lien_holder_name",
      "insurance.1.lien_holder_first_name",
      "insurance.1.lien_holder_last_name",
    ],
  });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const timeout = setTimeout(async () => {
      try {
        const response = await api_without_cancellation.get(
          "/api/general/search_filter_lien_company_directoires/",
          { params: { query } }
        );
        if (active) setResults(response.data?.data || []);
      } catch {
        if (active) setResults([]);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [query]);

  const selectHolder = (holder) => {
    const values = contactValues(
      "lien_holder",
      {
        ...holder,
        name: holder.company_name || holder.name,
        phone_number: holder.phone_number || holder.phone,
        phone_ext: holder.phone_ext || holder.extension,
      },
      holder.id
    );
    for (const [field, value] of Object.entries(values)) {
      setValue(`insurance.1.${field}`, value);
    }
    setValue("insurance.1.lien_holder_id", null);
    setResults([]);
    setQuery("");
  };

  const clearHolder = () => {
    for (const [field, value] of Object.entries(contactValues("lien_holder"))) {
      setValue(`insurance.1.${field}`, value);
    }
    setValue("insurance.1.lien_holder_id", null);
    setResults([]);
    setQuery("");
  };

  return (
    <InsurancePartyFieldset
      prefix="lien_holder"
      entityId={1}
      panelName="Lien Holder"
      panelStyle={{ width: 260 }}
      dynamicLabel="Company Name"
      searchPlaceholder="Type Lien Holder name to search directory then click an entry"
      statesData={states}
      search={{
        results,
        onSearchKeyUp: (event) => setQuery(event.currentTarget.value),
        applySelection: (id) => {
          const holder = results.find((result) => String(result.id) === String(id));
          if (holder) selectHolder(holder);
        },
      }}
      clear={clearHolder}
      computeDisplayName={() => name || `${firstName || ""} ${lastName || ""}`.trim()}
      renderNameRow={(fieldName) => (
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div className="d-flex-1 p-r-5">
            <input
              type="text"
              placeholder="Company Name"
              className="form-control rounded-0 height-25"
              {...register(fieldName("name"))}
            />
          </div>
          <div className="d-flex-1 p-r-5">
            <input
              type="text"
              placeholder="First Name"
              className="form-control rounded-0 height-25"
              {...register(fieldName("first_name"))}
            />
          </div>
          <div className="d-flex-1 p-r-5">
            <input
              type="text"
              placeholder="Last Name"
              className="form-control rounded-0 height-25"
              {...register(fieldName("last_name"))}
            />
          </div>
        </div>
      )}
    />
  );
};

const AddInsurancePopup = ({
  show,
  handleClose,
  onCreated,
  insurance = null,
  onDelete,
  initialTab = "parties",
  initialParty = null,
  preferInitialParty = false,
  partyScope = "all",
  billData = null,
}) => {
  const dispatch = useDispatch();
  const clientId = getClientId();
  const caseId = getCaseId();
  const methods = useForm({
    defaultValues: getFormValues(insurance),
  });
  const { getValues, handleSubmit, register, setValue, watch } = methods;
  const initialSelection =
    partyScope === "client" && !insurance?.id && initialTab === "type"
      ? null
      : getSelectedParty(insurance, initialParty, preferInitialParty);
  const [selectedParty, setSelectedParty] = useState(initialSelection);
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = getInitialTab(initialTab);
    if (!initialSelection?.id) return "parties";
    if (!getInsuranceTypeId(insurance)) return "type";
    return requestedTab;
  });
  const [parties, setParties] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [adjusterSection, setAdjusterSection] = useState(
    initialTab === "supervisor" ? "supervisor" : "adjuster"
  );
  const [lienSection, setLienSection] = useState(
    initialTab === "lien" ? "adjuster" : initialTab === "lien_holder" ? "holder" : "information"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const selectedTypeId = watch("insurance.1.insurance_type_id");
  const hasSelectedParty = Boolean(selectedParty?.id);
  const hasSelectedType = Number(selectedTypeId) > 0;
  const canOpenTab = (tab) =>
    tab === "parties" || (hasSelectedParty && (tab === "type" || hasSelectedType));

  const handleTabSelect = (tab) => {
    if (!canOpenTab(tab)) {
      setActiveTab(hasSelectedParty ? "type" : "parties");
      setError(hasSelectedParty ? "Choose an insurance type first." : "Choose a party first.");
      return;
    }
    setError("");
    setActiveTab(tab);
  };

  useEffect(() => {
    if (!show || !clientId || !caseId) return;
    let active = true;
    const fetchOptions = async () => {
      const [partiesResult, typesResult, statesResult] = await Promise.allSettled([
        api_without_cancellation.get(
          `/api/litigation-page/defendants-and-other-parties/${clientId}/${caseId}/`,
          { params: { litigation_event_popup: true } }
        ),
        api_without_cancellation.get("/api/general/insurance_types/"),
        api_without_cancellation.get("/api/states/"),
      ]);
      if (!active) return;
      if (partiesResult.status === "fulfilled") {
        let availableParties = getParties(partiesResult.value.data).filter(
          (party) => partyScope !== "client" || party.type === "Client"
        );
        if (partyScope === "client" && !insurance?.id && initialTab === "type") {
          setSelectedParty(availableParties.length === 1 ? availableParties[0] : null);
          setActiveTab(availableParties.length === 1 ? "type" : "parties");
        } else {
          let matchingParty = findPartyForInsurance(
            availableParties,
            insurance,
            initialParty,
            partyScope,
            preferInitialParty
          );
          if (!matchingParty && insurance?.id) {
            const assignedParty = getSelectedParty(insurance, initialParty, preferInitialParty);
            if (assignedParty?.id && (partyScope !== "client" || assignedParty.type === "Client")) {
              matchingParty = {
                ...assignedParty,
                name:
                  !assignedParty.name || assignedParty.name === "Unnamed party"
                    ? `${assignedParty.type} #${assignedParty.id}`
                    : assignedParty.name,
              };
              availableParties = [...availableParties, matchingParty];
            }
          }
          setSelectedParty(matchingParty);
          if (!matchingParty) setActiveTab("parties");
          else if (!Number(getValues("insurance.1.insurance_type_id"))) setActiveTab("type");
          else if (insurance?.id) setActiveTab(getInitialTab(initialTab));
        }
        setParties(availableParties);
      } else {
        setError("Unable to load case parties. Please try again.");
      }
      if (typesResult.status === "fulfilled") {
        const data = typesResult.value.data;
        setInsuranceTypes(Array.isArray(data) ? data : data?.data || []);
      } else {
        setError("Unable to load insurance types. Please try again.");
      }
      if (statesResult.status === "fulfilled") {
        setStates(statesResult.value.data);
      }
    };
    fetchOptions();
    return () => {
      active = false;
    };
  }, [
    show,
    clientId,
    caseId,
    initialParty?.id,
    initialParty?.type,
    insurance?.id,
    initialTab,
    partyScope,
    preferInitialParty,
    getValues,
  ]);

  const saveInsurance = async (values) => {
    if (!selectedParty?.id) {
      setActiveTab("parties");
      setError("Choose a party for this insurance.");
      return;
    }
    if (!hasSelectedType) {
      setActiveTab("type");
      setError("Choose an insurance type.");
      return;
    }

    const fields = values.insurance?.[1] || {};
    const insuranceClientId = selectedParty?.type === "Client" ? selectedParty.id : clientId;
    const payload = {
      ...fields,
      insurance_type_id: Number(selectedTypeId),
      confirmed_date: fields.confirmed_date || null,
      client_id: insuranceClientId,
      case_id: caseId,
    };
    const recordedParty = getPartyIdentity(insurance);
    const originalParty =
      recordedParty?.ids.length || !initialParty?.id
        ? recordedParty
        : { type: normalizePartyType(initialParty.type), ids: [initialParty.id] };
    const partyChanged =
      insurance?.id &&
      (originalParty?.type !== selectedParty.type ||
        !originalParty.ids.some((id) => String(id) === String(selectedParty.id)));
    if (partyChanged) {
      payload.defendant_id = selectedParty.type === "Defendant" ? selectedParty.id : null;
      payload.party_id = selectedParty.type === "OtherParty" ? selectedParty.id : null;
      payload.witness_id = selectedParty.type === "Witness" ? selectedParty.id : null;
    } else if (!insurance?.id) {
      if (selectedParty.type === "Defendant") payload.defendant_id = selectedParty.id;
      if (selectedParty.type === "OtherParty") payload.party_id = selectedParty.id;
      if (selectedParty.type === "Witness") payload.witness_id = selectedParty.id;
    }

    for (const field of ["company", "adjuster", "lien_adjuster", "lien_holder"]) {
      if (payload[`${field}_extension`] !== undefined) {
        payload[`${field}_extension_field`] = payload[`${field}_extension`];
        delete payload[`${field}_extension`];
      }
    }
    for (const [field, value] of Object.entries(payload)) {
      if (field.startsWith("supervisor_adjuster_")) {
        payload[field.replace("supervisor_adjuster_", "supervisor_")] = value;
        delete payload[field];
      }
    }
    if (payload.supervisor_extension !== undefined) {
      payload.supervisor_extension_field = payload.supervisor_extension;
      delete payload.supervisor_extension;
    }
    for (const field of [
      "company_first_name",
      "company_last_name",
      "adjuster_name",
      "supervisor_name",
      "lien_adjuster_name",
    ]) {
      delete payload[field];
    }

    setLoading(true);
    setError("");
    try {
      if (insurance?.id) {
        await dispatch(editInsurance({ ...payload, insurance_id: insurance.id })).unwrap();
      } else {
        await dispatch(
          createInsurance({ client_id: insuranceClientId, case_id: caseId, data: payload })
        ).unwrap();
      }
      onCreated?.();
      handleClose();
    } catch (saveError) {
      setError(saveError?.detail || "Unable to save insurance. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Modal show={show} onHide={handleClose} centered dialogClassName="add-insurance-dialog">
        <Modal.Header className="tfbp-modal-header">
          <Modal.Title className="tfbp-modal-title text-uppercase">
            {insurance?.id ? "Edit Insurance" : "Add Insurance"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="add-insurance-body p-0">
          <div className="add-insurance-nav invisible-scrollbar">
            <Nav activeKey={activeTab} onSelect={handleTabSelect} className="add-insurance-tabs">
              {STEPS.map((step) => (
                <Nav.Link
                  key={step.id}
                  eventKey={step.id}
                  disabled={!canOpenTab(step.id)}
                  className="add-insurance-tab d-flex align-items-center justify-content-center"
                >
                  {step.label}
                </Nav.Link>
              ))}
            </Nav>
          </div>
          <form id="add-insurance-form" onSubmit={handleSubmit(saveInsurance)}>
            <input type="hidden" {...register("insurance.1.insurance_type_id")} />
            <div className="add-insurance-panel">
              {activeTab === "parties" && (
                <>
                  <div className="m-t-5 m-b-5 p-l-5 p-r-5">
                    {PARTY_GROUPS.map((group) => {
                      const groupParties = parties.filter((party) => party.type === group.type);
                      if (!groupParties.length) return null;
                      return (
                        <div
                          key={group.type}
                          className={`row mx-auto ps-0 pe-0 align-items-start m-b-5 ${group.type === "Client" ? "" : "m-t-10"}`}
                        >
                          <div className="col-2 p-r-5 text-start">
                            <span className="d-inline-block text-primary-25">
                              <nobr>{group.label}</nobr>
                            </span>
                          </div>
                          <div className="col-10 ps-0 pe-0">
                            <div className="add-insurance-party-grid">
                              {groupParties.map((party) => {
                                const selected =
                                  selectedParty?.type === party.type &&
                                  String(selectedParty?.id) === String(party.id);
                                return (
                                  <button
                                    type="button"
                                    key={`${party.type}-${party.id}`}
                                    className={`event-type-button height-25 add-insurance-party-button ${selected ? "selected text-white" : "unselected text-primary-25"}`}
                                    style={{
                                      fontWeight: "600",
                                    }}
                                    aria-pressed={selected}
                                    onClick={() => {
                                      setSelectedParty(party);
                                      setError("");
                                    }}
                                  >
                                    <i className={`ic ic-15 ${group.icon} m-r-5`} />
                                    <span className="text-nowrap add-insurance-party-name">
                                      {party.name}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {activeTab === "type" && (
                <>
                  <div
                    className="add-insurance-choice-grid add-insurance-type-grid"
                    style={{
                      padding: "5px",
                    }}
                  >
                    {insuranceTypes.map((type) => (
                      <button
                        type="button"
                        key={type.id}
                        className={`event-type-button3  justify-content-center height-25 add-insurance-choice ${String(selectedTypeId) === String(type.id) ? "selected text-white" : "unselected text-primary-25"}`}
                        aria-pressed={String(selectedTypeId) === String(type.id)}
                        onClick={() => {
                          setValue("insurance.1.insurance_type_id", Number(type.id), {
                            shouldDirty: true,
                          });
                          setError("");
                        }}
                      >
                        <i className="ic ic-15 ic-insurance m-r-5" />
                        <span className="text-nowrap add-insurance-choice-name">{type.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {activeTab === "company" && (
                <>
                  <InsuranceCompanyFields entityId={1} stateAbrs={states} />
                </>
              )}
              {activeTab === "adjuster" && (
                <>
                  <div
                    className="add-insurance-choice-grid add-insurance-adjuster-choices"
                    style={{
                      paddingTop: "5px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                    }}
                  >
                    <button
                      type="button"
                      className={`event-type-button3 btn-hover-event-litigation-act justify-content-center height-25 add-insurance-choice ${adjusterSection === "adjuster" ? "selected text-white" : "unselected text-primary-25"}`}
                      aria-pressed={adjusterSection === "adjuster"}
                      onClick={() => setAdjusterSection("adjuster")}
                    >
                      <i className="ic ic-15 ic-insurance m-r-5" />
                      Adjuster
                    </button>
                    <button
                      type="button"
                      className={`event-type-button3 btn-hover-event-litigation-act justify-content-center height-25 add-insurance-choice ${adjusterSection === "supervisor" ? "selected text-white" : "unselected text-primary-25"}`}
                      aria-pressed={adjusterSection === "supervisor"}
                      onClick={() => setAdjusterSection("supervisor")}
                    >
                      <i className="ic ic-15 ic-insurance m-r-5" />
                      Supervisor
                    </button>
                  </div>
                  {adjusterSection === "adjuster" ? (
                    <InsuranceAdjusterFields entityId={1} stateAbrs={states} />
                  ) : (
                    <InsuranceSupervisorFields entityId={1} stateAbrs={states} />
                  )}
                </>
              )}
              {activeTab === "claim" && (
                <>
                  <ClaimFields />
                </>
              )}
              {activeTab === "lien" && (
                <>
                  <div
                    className="add-insurance-choice-grid add-insurance-lien-choices"
                    style={{
                      padding: "5px 5px 0px 5px",
                    }}
                  >
                    <button
                      type="button"
                      className={`event-type-button3 btn-hover-event-litigation-act justify-content-center height-25 add-insurance-choice ${lienSection === "information" ? "selected" : "unselected"}`}
                      aria-pressed={lienSection === "information"}
                      onClick={() => setLienSection("information")}
                    >
                      <i className="ic ic-15 ic-insurance m-r-5" />
                      Lien Information
                    </button>
                    <button
                      type="button"
                      className={`event-type-button3 btn-hover-event-litigation-act justify-content-center height-25 add-insurance-choice ${lienSection === "adjuster" ? "selected" : "unselected"}`}
                      aria-pressed={lienSection === "adjuster"}
                      onClick={() => setLienSection("adjuster")}
                    >
                      <i className="ic ic-15 ic-insurance m-r-5" />
                      Lien Adjuster
                    </button>
                    <button
                      type="button"
                      className={`event-type-button3 btn-hover-event-litigation-act justify-content-center height-25 add-insurance-choice ${lienSection === "holder" ? "selected" : "unselected"}`}
                      aria-pressed={lienSection === "holder"}
                      onClick={() => setLienSection("holder")}
                    >
                      <i className="ic ic-15 ic-insurance m-r-5" />
                      Lien Holder
                    </button>
                  </div>
                  {lienSection === "information" ? (
                    <>
                      {billData && (
                        <div className="add-insurance-bill-summary">
                          <span>HI Paid: ${billData.total_ins_paid || "0.00"}</span>
                          <span>Medpay/PIP: ${billData.total_medpaypaip || "0.00"}</span>
                        </div>
                      )}
                      <label className="add-insurance-check m-l-5 height-25">
                        <input type="checkbox" {...register("insurance.1.ERISAYN")} />
                        ERISA Protected Lien
                      </label>
                      <InsuranceLienInformationFields entityId={1} />
                    </>
                  ) : lienSection === "adjuster" ? (
                    <InsuranceLienAdjusterFields entityId={1} stateAbrs={states} />
                  ) : (
                    <LienHolderFields states={states} />
                  )}
                </>
              )}
            </div>
          </form>
          {error && (
            <div className="add-insurance-error" role="alert">
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="add-insurance-footer">
          <Button
            type="button"
            variant="secondary"
            className="height-25 d-flex align-items-center justify-content-center"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          {insurance?.id && onDelete && (
            <Button
              type="button"
              variant="danger"
              className="height-25 d-flex align-items-center justify-content-center"
              onClick={() => onDelete(insurance.id)}
              disabled={loading}
            >
              Delete
            </Button>
          )}
          <Button
            type="submit"
            form="add-insurance-form"
            variant="success"
            className="height-25 d-flex align-items-center justify-content-center"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save & Close"}
          </Button>
        </Modal.Footer>
      </Modal>
    </FormProvider>
  );
};

export default AddInsurancePopup;
